import axios from "axios";
import { env } from "../constants/env";
import { useSessionStore } from "../stores/sessionStore";
import { performLogout, refreshAccessTokens } from "../../features/auth/services/keycloakAuthService";

export const apiClient = axios.create({
  baseURL: env.gatewayUrl,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
});

// Injection automatique du Bearer token sur chaque requête
apiClient.interceptors.request.use((config) => {
  const token = useSessionStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;

  // ⚠️ Un envoi multipart ne doit PAS porter le `application/json` par défaut
  // de l'instance.
  //
  // Le boundary d'un corps multipart est généré par le navigateur au moment de
  // sérialiser le `FormData` — il fait partie intégrante de l'en-tête
  // (`multipart/form-data; boundary=…`). En laissant `application/json`, le
  // serveur recevait bien les octets du fichier mais tentait de les lire comme
  // du JSON : la requête était rejetée en 400 et AUCUN fichier n'arrivait.
  // C'est ce qui empêchait les agents d'envoyer leurs photos du Bouclier
  // Qualité — le backend voyait une requête sans image.
  //
  // Mettre l'en-tête à `undefined` le retire : axios laisse alors le navigateur
  // poser le type complet, boundary compris. La garde vit ICI, au point de
  // passage commun, plutôt que dans chaque service qui téléverse.
  if (typeof FormData !== "undefined" && config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }

  return config;
});

// ─── Unwrap `{ data, meta? }` du backend ────────────────────────────────────
// Depuis openapi v2 (juin 2026), la plupart des réponses sont enveloppées :
//   { data: <payload>, meta?: { total?: number }, success?, message?, metadata? }
// On déballe automatiquement au niveau interceptor pour que les services
// existants continuent à recevoir le payload directement (rétrocompat avec la
// signature `apiClient.get<T>` → response.data: T).
//
// Convention : on déballe SI `response.data.data` est défini ET que la réponse
// n'a pas une signature d'erreur (`success === false` ou `error: { code }`).
// Les endpoints "flat" (auth/refresh, auth/exchange, health) ne sont pas touchés.
// Les Errors HTTP passent par l'interceptor d'erreur ci-dessous, pas par celui-ci.
//
// `meta` (pagination) est attaché à `response.meta` pour les consommateurs qui
// en ont besoin (listes paginées). À typer côté caller via une assertion.
apiClient.interceptors.response.use((response) => {
  const body = response.data;
  if (
    body !== null &&
    typeof body === "object" &&
    "data" in body &&
    body.success !== false
  ) {
    const wrapped = body as { data: unknown; meta?: unknown; pagination?: unknown };
    response.data = wrapped.data;
    (response as unknown as { meta?: unknown }).meta = wrapped.meta;
    (response as unknown as { pagination?: unknown }).pagination = wrapped.pagination;
  }
  return response;
});

// Gestion des 401 : refresh token puis retry automatique.
// Le singleton dans refreshAccessTokens() garantit qu'un seul appel réseau
// est effectué même si plusieurs requêtes reçoivent 401 simultanément.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // On ne tente le refresh que pour les 401 et une seule fois par requête
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const { refreshToken, updateTokens } = useSessionStore.getState();
    if (!refreshToken) {
      performLogout();
      return Promise.reject(error);
    }

    try {
      const tokens = await refreshAccessTokens(refreshToken);

      updateTokens(
        tokens.access_token,
        tokens.refresh_token,
        Date.now() + tokens.expires_in * 1000,
        tokens.refresh_expires_in > 0
          ? Date.now() + tokens.refresh_expires_in * 1000
          : 0,
      );

      originalRequest._retry = true;
      originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`;
      return apiClient(originalRequest);
    } catch {
      // Refresh définitivement échoué (token révoqué, session admin invalidée…)
      // performLogout est synchrone sur clearSession → RootNavigator bascule vers AuthStack
      performLogout();
      return Promise.reject(error);
    }
  },
);

// ─── Log DEV des retours API ────────────────────────────────────────────────
// Trace CHAQUE réponse API en développement : méthode, URL, status, corps.
// But : suivre un flow de bout en bout — ex. « Lancer la course » →
//   POST /trips → 201, puis le polling GET /trips/{id} qui montre la
//   progression des statuts (WALLET_FUNDS_BLOCKED → AGENT_ASSIGNED → …).
// Enregistré EN DERNIER → voit la réponse finale (données déjà déballées par
// l'interceptor ci-dessus, retry 401 déjà rejoué). Filtre logs : « [API ».
// console.warn : ESLint interdit console.log dans ce projet.
if (env.isDev) {
  const short = (data: unknown): unknown => {
    try {
      const s = JSON.stringify(data);
      return s && s.length > 1500 ? `${s.slice(0, 1500)}… (${s.length} chars)` : data;
    } catch {
      return data;
    }
  };

  // Reconstruit l'URL AVEC la query string : axios met les filtres dans
  // `config.params` (pas dans `config.url`). Sans ça, un `GET /trips?agent_id=…`
  // s'affiche « GET /trips » et on croit à tort que le param n'est pas envoyé.
  const fullUrl = (config: { url?: string; params?: unknown }): string => {
    const base   = config.url ?? "";
    const params = config.params as Record<string, unknown> | undefined;
    if (!params) return base;
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
      .join("&");
    return qs ? `${base}?${qs}` : base;
  };

  apiClient.interceptors.response.use(
    (response) => {
      const { method } = response.config;
      console.warn(
        `[API ✓] ${(method ?? "get").toUpperCase()} ${fullUrl(response.config)} → ${response.status}`,
        short(response.data),
      );
      return response;
    },
    (error) => {
      const cfg = error.config ?? {};
      const status = error.response?.status ?? "réseau/timeout";
      console.warn(
        `[API ✗] ${(cfg.method ?? "get").toUpperCase()} ${fullUrl(cfg)} → ${status}`,
        short(error.response?.data ?? error.message),
      );
      return Promise.reject(error);
    },
  );
}
